import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageContainer from "components/ui/page-container";
import PageHeader from "components/ui/PageHeader";
import { ArrowLeft, ContactRound } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  UTILSService,
  createCustomersRes,
  updateCustomersRes,
  getCustomerByIdRes,
} from "core";
import { toast } from "components/ui/toast";
import { LeadFormSkeleton } from "skeleton/leads/LeadsSkeleton";
import { SkeletonSwitch } from "skeleton";

type LeadFormProps = {
  isModal?: boolean;
  onClose?: () => void;
  onSave?: () => void;
};

type RouteParams = {
  group?: string;
  groupName?: string;
  id?: string;
};

export default function LeadForm({ isModal = false, onClose, onSave }: LeadFormProps) {
  const navigate = useNavigate();
  const params = useParams<RouteParams>();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setInitialLoading] = useState(false);

  // form state
  const [groupName, setGroupName] = useState("");
  const [groupId, setGroupId] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [messengerId, setMessengerId] = useState("");
  const [email, setEmail] = useState("");

  const [leadsId, setLeadsId] = useState<string | null>(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isNewLeadGroup, setIsNewLeadGroup] = useState(false);

  // error flags
  const [editGroupName, setEditGroupName] = useState(false);
  const [editFirstName, setEditFirstName] = useState(false);
  const [editLastName, setEditLastName] = useState(false);
  const [editEmail, setEditEmail] = useState(false);

  // ---------- helpers ----------
  const acceptNumber = useCallback((value: string) => {
    const cleaned = value.replace(/\D/g, "");
    const match = cleaned.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    if (!match) return value;
    const [, a, b, c] = match;
    return !b ? a : `(${a}) ${b}${c ? "-" + c : ""}`;
  }, []);

  /** Validate fields */
  const validateNow = useCallback(() => {
    let groupNameErr = false;

    // only enforce groupName if creating brand new group
    if (isNewLeadGroup) {
      groupNameErr = !groupName.trim();
      setEditGroupName(groupNameErr);
    }

    const firstNameErr = !firstName.trim();
    const lastNameErr = !lastName.trim();
    const emailErr = !UTILSService.EMAIL_REGEX.test(email);

    setEditFirstName(firstNameErr);
    setEditLastName(lastNameErr);
    setEditEmail(emailErr);

    return !(groupNameErr || firstNameErr || lastNameErr || emailErr);
  }, [isNewLeadGroup, groupName, firstName, lastName, email]);

  const resetForm = useCallback(() => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setGroupName("");
    setWhatsappNumber("");
    setPhoneNumber("");
    setMessengerId("");
    setIsFormSubmitted(false);

    setEditGroupName(false);
    setEditFirstName(false);
    setEditLastName(false);
    setEditEmail(false);
  }, []);

  const navigateTo = useCallback(() => {
    if (isModal) {
      onClose?.();
      return;
    }
    const currentPage = Number(searchParams.get("page") || "1");
    navigate({ pathname: "/leads", search: `?page=${currentPage}` });
  }, [isModal, onClose, navigate, searchParams]);

  // ---------- API ----------
  const getLeadsById = useCallback(async (id: string) => {
    try {
      setInitialLoading(true);
      const res = await getCustomerByIdRes(id);
      const lead = res?.data?.[0];
      if (lead) {
        setGroupName(lead.group?.name || "");
        setGroupId(lead.group?.id || params.group || null);
        setFirstName(lead.firstName || "");
        setLastName(lead.lastName || "");
        setWhatsappNumber(lead.whatsappNumber || "");
        setMessengerId(lead.messengerId || "");
        setPhoneNumber(lead.phoneNumber || "");
        setEmail(lead.email || "");
        setLeadsId(lead.id || null);
      }
    } catch {
      toast.error("Something went wrong!");
    } finally {
      setInitialLoading(false);
    }
  }, [params.group]);

  const createOrUpdate = useCallback(async () => {
    // figure out final group name (params or state)
    const finalGroupName =
      isNewLeadGroup ? groupName : params.groupName || groupName;

    const data: any = [
      {
        groupName: finalGroupName,
        groupId,
        customers: [
          {
            firstName,
            lastName,
            whatsappNumber,
            messengerId,
            email,
            phoneNumber,
          },
        ],
      },
    ];

    setIsLoading(true);
    try {
      if (!leadsId) {
        await createCustomersRes(data);
        toast.success("Created successfully!");
        resetForm();
        onSave();
        navigateTo();
        if (isModal) onClose?.();
      } else {
        const updateData = {
          id: leadsId,
          firstName,
          lastName,
          whatsappNumber,
          messengerId,
          email,
          phoneNumber,
          groupId,
        };
        await updateCustomersRes(updateData);
        toast.success("Updated successfully!");
        navigateTo();
        if(onSave) onSave?.();
        if (isModal) onClose?.();
      }
    } catch (e: any) {
      toast.error(String(e?.message || e) || "Request failed");
    } finally {
      setIsLoading(false);
    }
  }, [
    isNewLeadGroup,
    groupName,
    params.groupName,
    groupId,
    firstName,
    lastName,
    whatsappNumber,
    messengerId,
    email,
    phoneNumber,
    leadsId,
    resetForm,
    isModal,
    onClose,
    navigateTo,
  ]);

  // ---------- lifecycle ----------
  useEffect(() => {
    // case: /leads/new
    if (!params.group && !params.id) {
      setIsNewLeadGroup(true);
    }

    // case: /leads/:group/:groupName/new
    if (params.group && params.id === "new") {
      setIsNewLeadGroup(false);
      setGroupId(params.group);
      if (params.groupName) setGroupName(params.groupName);
    }

    // case: /leads/:group/:groupName/:id (edit)
    if (params.id && params.id !== "new") {
      setIsNewLeadGroup(false);
      if (params.group) setGroupId(params.group);
      if (params.id) getLeadsById(params.id);
    }
  }, [params, getLeadsById]);

  // ---------- submit ----------
  const onSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      setIsFormSubmitted(true);
      if (!validateNow()) return;
      createOrUpdate();
    },
    [validateNow, createOrUpdate]
  );

  const onFormInvalid = useCallback(
    (_e: React.FormEvent<HTMLFormElement>) => {
      setIsFormSubmitted(true);
      validateNow();
    },
    [validateNow]
  );

  // field handlers
  const handleGroupNameChange = (v: string) => {
    setGroupName(v);
    if (isFormSubmitted) setEditGroupName(!v.trim());
  };
  const handleFirstNameChange = (v: string) => {
    setFirstName(v);
    if (isFormSubmitted) setEditFirstName(!v.trim());
  };
  const handleLastNameChange = (v: string) => {
    setLastName(v);
    if (isFormSubmitted) setEditLastName(!v.trim());
  };
  const handleEmailChange = (v: string) => {
    setEmail(v);
    if (isFormSubmitted) setEditEmail(!UTILSService.EMAIL_REGEX.test(v));
  };

  const onReset = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      resetForm();
    },
    [resetForm]
  );

  const pageTitle = `${leadsId ? "Update" : "Create New"} Lead`;

  return (
    <PageContainer>
      <SkeletonSwitch isLoading={isInitialLoading} skeleton={<LeadFormSkeleton />}>
      <div className="mx-auto">
        {/* The back control keeps its own handler: it returns to the list at
            the page you left it (`?page=N`), which the shell's breadcrumb —
            a plain link to /leads — cannot do. */}
        <PageHeader
          icon={<ContactRound className="w-5 h-5" />}
          tone="brand"
          title={pageTitle}
          leading={
            !isModal ? (
              <button
                type="button"
                onClick={navigateTo}
                aria-label="Back to leads"
                className="mt-0.5 inline-flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-xl text-content-muted hover:text-content hover:bg-surface-hover transition-colors focus-ring"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            ) : null
          }
        />

        {/* Card */}
        <form
          onInvalid={onFormInvalid}
          onSubmit={onSubmit}
          onReset={onReset}
          className="rounded-2xl border border-line bg-surface p-5 shadow-sm"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Group Name */}
            <div className="space-y-2">
              <label
                htmlFor="group-name"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                Group Name
              </label>

              {isNewLeadGroup ? (
                <Input
                  id="group-name"
                  name="groupName"
                  value={groupName}
                  onChange={(e) => handleGroupNameChange(e.target.value)}
                  aria-invalid={editGroupName || undefined}
                  placeholder="Enter group name"
                  required
                />
              ) : (
                <Input
                  id="group-name"
                  name="groupName"
                  value={params.groupName || groupName || ""}
                  readOnly
                  disabled
                />
              )}

              {editGroupName && (
                <p className="text-xs text-red-600 dark:text-red-300">Group Name is required</p>
              )}
            </div>

            {/* First Name */}
            <div className="space-y-2">
              <label
                htmlFor="customer-first-name"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                First Name
              </label>
              <Input
                id="customer-first-name"
                name="firstName"
                value={firstName}
                onChange={(e) => handleFirstNameChange(e.target.value)}
                aria-invalid={editFirstName || undefined}
                placeholder="Enter first name"
                required
              />
              {editFirstName && (
                <p className="text-xs text-red-600 dark:text-red-300">
                  Customer First Name is required
                </p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <label
                htmlFor="customer-second-name"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                Last Name
              </label>
              <Input
                id="customer-second-name"
                name="lastName"
                value={lastName}
                onChange={(e) => handleLastNameChange(e.target.value)}
                aria-invalid={editLastName || undefined}
                placeholder="Enter last name"
                required
              />
              {editLastName && (
                <p className="text-xs text-red-600 dark:text-red-300">
                  Customer Last Name is required
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label
                htmlFor="customer-email"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                Email
              </label>
              <Input
                id="customer-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                aria-invalid={editEmail || undefined}
                placeholder="Enter customer email"
                required
              />
              {editEmail && (
                <p className="text-xs text-red-600 dark:text-red-300">Enter valid email</p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label
                htmlFor="customer-mobile"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                Mobile No
              </label>
              <Input
                id="customer-mobile"
                name="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(acceptNumber(e.target.value))}
                maxLength={14}
                placeholder="Enter customer mobile"
              />
            </div>

            {/* Whatsapp */}
            <div className="space-y-2">
              <label
                htmlFor="customer-whatsapp"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                Whatsapp Number
              </label>
              <Input
                id="customer-whatsapp"
                name="whatsappNumber"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(acceptNumber(e.target.value))}
                maxLength={14}
                placeholder="Enter WhatsApp number"
              />
            </div>

            {/* Messenger Id */}
            <div className="space-y-2 md:col-span-2">
              <label
                htmlFor="customer-messenger"
                className="text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide"
              >
                Messenger Id
              </label>
              <Input
                id="customer-messenger"
                name="messengerId"
                value={messengerId}
                onChange={(e) => setMessengerId(e.target.value)}
                placeholder="Enter messenger id"
              />
            </div>
          </div>

          {/* Footer actions */}
          <div className="mt-6 flex items-center justify-end gap-3 border-t border-line-subtle dark:border-dark-700 pt-4">
            <Button
              type="button"
              variant="destructive"
              className="rounded-lg"
              onClick={navigateTo}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="rounded-lg"
              loading={isLoading}
            >
              {leadsId ? "Update Lead" : "Save & Add New Lead"}
            </Button>
          </div>
        </form>
      </div>
    </SkeletonSwitch>
    </PageContainer>
  );
}
